import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, newIdempotencyKey } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { M14_ROLES, hasAnyRole } from '../../lib/roles';
import type { ApplicantType, LicenceApplication, LicenceClass, LicenceClassCode, Page } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, InfoNote, PageHeader, Spinner, StatusBadge, Tabs } from '../../ui/components';

const TABS = [
  { key: 'apply', label: 'Apply' },
  { key: 'mine', label: 'My applications' },
  { key: 'all', label: 'All applications (staff)' },
] as const;

export function LicenceApplications() {
  const { persona } = useAuth();
  const isOfficer = hasAnyRole(persona?.roles, M14_ROLES.LICENSING_OFFICER, M14_ROLES.SENIOR_LO, M14_ROLES.BOARD_SECRETARIAT, M14_ROLES.ENFORCEMENT_OFFICER);
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('apply');

  return (
    <div>
      <PageHeader title="Licence Applications" subtitle="Apply for an SLA licence — the engine resolves requirements (Sey-X checks, uploads, inspections, board) automatically." />
      <Tabs tabs={isOfficer ? TABS : TABS.filter((t) => t.key !== 'all')} value={tab} onChange={setTab} />
      {tab === 'apply' ? <ApplyForm /> : tab === 'mine' ? <MineApplications /> : <AllApplications />}
    </div>
  );
}

function ApplyForm() {
  const { persona } = useAuth();
  const classesQ = useApiQuery<Page<LicenceClass>>('/api/v1/m14/classes?limit=100&status=ACTIVE');
  const options: Array<{ type: ApplicantType; id: string; label: string }> = [];
  if (persona?.nin) options.push({ type: 'PERSON', id: persona.nin, label: `Myself (${persona.nin})` });
  if (persona?.actingForBusinessId) options.push({ type: 'ENTITY', id: persona.actingForBusinessId, label: `My business (${persona.actingForBusinessId})` });

  // Hooks called unconditionally — see Permits.tsx's ApplyForm for why.
  const [applicantIdx, setApplicantIdx] = useState(0);
  const [classCode, setClassCode] = useState<LicenceClassCode | ''>('');
  const [premisesAddress, setPremisesAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<LicenceApplication | null>(null);

  if (!persona) return <InfoNote message="Sign in to apply for a licence." />;
  if (options.length === 0) return <InfoNote message="Your account has no NIN or business mandate on file — sign in as a trader/investor persona." />;

  const submit = async () => {
    if (!classCode) return;
    setBusy(true);
    setError(null);
    try {
      const applicant = options[applicantIdx]!;
      const res = await api.post<LicenceApplication>(
        '/api/v1/m14/applications',
        { classCode, applicantType: applicant.type, applicantId: applicant.id, premisesAddress: premisesAddress || undefined },
        { actingFor: applicant.type === 'ENTITY' ? applicant.id : undefined, idempotencyKey: newIdempotencyKey() },
      );
      setCreated(res);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Application failed');
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <div className={`card p-5 ${created.state === 'REJECTED' ? 'border-rose-200 bg-rose-50' : 'border-emerald-200 bg-emerald-50'}`}>
        <h2 className="text-sm font-bold">Application submitted — {created.state}</h2>
        <p className="mt-1 text-xs text-slate-600">{(created.requirementResults ?? []).length} requirement(s) to resolve.</p>
        <Link to={`/licensing/applications/${created.id}`} className="btn-primary mt-3 inline-flex">View application</Link>
      </div>
    );
  }

  return (
    <div className="card p-5">
      {error ? <div className="mb-3"><ErrorNote message={error} /></div> : null}
      <div className="space-y-3">
        {options.length > 1 ? (
          <Field label="Applying as">
            <select className="field-input" value={applicantIdx} onChange={(e) => setApplicantIdx(Number(e.target.value))}>
              {options.map((o, i) => <option key={o.id} value={i}>{o.label}</option>)}
            </select>
          </Field>
        ) : null}
        <Field label="Licence class">
          <select className="field-input" value={classCode} onChange={(e) => setClassCode(e.target.value as LicenceClassCode)}>
            <option value="">Choose a licence class…</option>
            {(classesQ.data?.items ?? []).map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="Premises address (optional)"><input className="field-input" value={premisesAddress} onChange={(e) => setPremisesAddress(e.target.value)} /></Field>
        <button className="btn-primary" onClick={submit} disabled={busy || !classCode}>{busy ? 'Submitting…' : 'Submit application'}</button>
      </div>
    </div>
  );
}

function MineApplications() {
  const { persona } = useAuth();
  const mineQ = useApiQuery<{ items: LicenceApplication[] }>('/api/v1/m14/applications/me', persona?.actingForBusinessId);
  return (
    <div>
      {mineQ.error ? <ErrorNote message={mineQ.error} /> : null}
      {mineQ.loading ? <Spinner /> : (mineQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No applications yet" /> : (
        <div className="space-y-2">
          {mineQ.data!.items.map((a) => (
            <Link key={a.id} to={`/licensing/applications/${a.id}`} className="card block p-4 hover:border-brand-300">
              <div className="flex items-center justify-between"><span className="text-sm font-semibold">{a.classCode.replaceAll('_', ' ')}</span><StatusBadge status={a.state} /></div>
              <div className="mt-1 text-xs text-slate-500">{(a.requirementResults ?? []).length} requirement(s)</div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

function AllApplications() {
  const [status, setStatus] = useState('');
  const [classCode, setClassCode] = useState('');
  const query = new URLSearchParams({ limit: '50' });
  if (status) query.set('status', status);
  if (classCode) query.set('classCode', classCode);
  const listQ = useApiQuery<Page<LicenceApplication>>(`/api/v1/m14/applications?${query.toString()}`);

  return (
    <div>
      <div className="mb-4 grid gap-2 sm:grid-cols-2">
        <Field label="Status" className="!mb-0">
          <select className="field-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {['APPLIED', 'REQUIREMENTS_GATHERING', 'ALL_MET', 'BOARD_AGENDA', 'FEE_PENDING', 'ISSUED', 'REJECTED', 'WITHDRAWN'].map((s) => <option key={s} value={s}>{s.replaceAll('_', ' ')}</option>)}
          </select>
        </Field>
        <Field label="Licence class" className="!mb-0"><input className="field-input" value={classCode} onChange={(e) => setClassCode(e.target.value.toUpperCase())} /></Field>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? <Spinner /> : (listQ.data?.items.length ?? 0) === 0 ? <EmptyState title="No applications match" /> : (
          <table className="w-full">
            <thead><tr><th className="table-head">Class</th><th className="table-head">Applicant</th><th className="table-head">Status</th></tr></thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50">
                  <td className="table-cell text-xs"><Link className="font-semibold text-brand-600 hover:underline" to={`/licensing/applications/${a.id}`}>{a.classCode.replaceAll('_', ' ')}</Link></td>
                  <td className="table-cell font-mono text-xs text-slate-500">{a.applicantId}</td>
                  <td className="table-cell"><StatusBadge status={a.state} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
